/**
 * Helpers for MatchResultCard: correct picks count, card type (pill) from entry.
 */

import type { PickHistoryEntry } from '../types/friends.types';
import { MATCH_WINNER_MARKET_ID } from '../../challenges/constants/challenges.constants';
import { getMatchDisplayStatusUpper } from '../../challenges/helpers/matchDisplayStatus.helper';

export interface CorrectPicks {
  userCorrect: number;
  friendCorrect: number;
}

/**
 * Count how many picks each side got correct across all markets (pointsEarned > 0).
 * Includes 1X2 (userPrediction/friendPrediction) plus other markets from userPicks/friendPicks.
 * Excludes 1X2 from userPicks/friendPicks count because backend duplicates it in userPrediction.
 */
export function getCorrectPicksFromEntry(entry: PickHistoryEntry): CorrectPicks {
  let userCorrect = 0;
  let friendCorrect = 0;
  for (const m of entry.matches) {
    if ((m.userPrediction?.pointsEarned ?? 0) > 0) userCorrect += 1;
    userCorrect += (m.userPicks ?? [])
      .filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID && (p.pointsEarned ?? 0) > 0)
      .length;
    if ((m.friendPrediction?.pointsEarned ?? 0) > 0) friendCorrect += 1;
    friendCorrect += (m.friendPicks ?? [])
      .filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID && (p.pointsEarned ?? 0) > 0)
      .length;
  }
  return { userCorrect, friendCorrect };
}

/** Card type for pill: Live, Active, Finished (sort order: live first, then active, then finished). */
export const CARD_TYPE = {
  LIVE: 'LIVE',
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED',
  CANCELLED: 'CANCELLED',
} as const;

export type CardTypeKey = keyof typeof CARD_TYPE;

const TERMINAL_MATCH_STATUSES = new Set(['FINISHED', 'CANCELLED', 'POSTPONED', 'ABANDONED']);
const LIVE_MATCH_STATUSES = new Set(['LIVE', 'IN_PLAY']);

/**
 * Derive card type from actual match statuses (not just challenge status).
 * Challenge status from the backend may lag behind reality (settlement delay).
 */
export function getCardTypeFromEntry(entry: PickHistoryEntry): { label: string; key: CardTypeKey } {
  if (entry.status === 'CANCELLED') return { label: 'Cancelled', key: 'CANCELLED' };
  if (entry.status === 'COMPLETED') return { label: 'Finished', key: 'FINISHED' };

  const matchStatuses = entry.matches.map((m) => String(m.matchResult.status ?? '').trim().toUpperCase());
  const allTerminal = matchStatuses.length > 0 && matchStatuses.every((s) => TERMINAL_MATCH_STATUSES.has(s));
  if (allTerminal) return { label: 'Finished', key: 'FINISHED' };

  const anyLive = matchStatuses.some((s) => LIVE_MATCH_STATUSES.has(s));
  if (anyLive) return { label: 'Live', key: 'LIVE' };

  const anyKickoffLive =
    entry.status === 'ACTIVE' &&
    entry.matches.some((m) => getMatchDisplayStatusUpper(m.matchResult.status, m.date) === 'LIVE');
  if (anyKickoffLive) return { label: 'Live', key: 'LIVE' };

  return { label: 'Active', key: 'ACTIVE' };
}
